/** LinkedIn hard limit for personalized connection request notes. */
export const INVITATION_CUSTOM_MESSAGE_MAX_LENGTH = 300;

/** Safe template limit when no merge variables are used (buffer under LinkedIn max). */
export const INVITATION_TEMPLATE_BASE_MAX_LENGTH = 290;

export const INVITE_MERGE_TOKEN_FIELDS = [
  { label: "First name", key: "firstName", help: "This variable is replaced with the prospect's first name." },
  { label: "Last name", key: "lastName", help: "This variable is replaced with the prospect's last name." },
  { label: "Position", key: "position", help: "This variable is replaced with the prospect's job position." },
  { label: "Company", key: "company", help: "This variable is replaced with the prospect's company name." },
  { label: "Location", key: "location", help: "This variable is replaced with the prospect's location." },
] as const;

export const MESSAGE_MERGE_TOKEN_FIELDS = [
  ...INVITE_MERGE_TOKEN_FIELDS,
  { label: "Video link", key: "videolink", help: "This variable is replaced with the prospect's video link." },
] as const;

export type MergeTokenField = (typeof INVITE_MERGE_TOKEN_FIELDS)[number];
export type InviteMergeTokenKey = MergeTokenField["key"];
export type MessageMergeTokenKey = (typeof MESSAGE_MERGE_TOKEN_FIELDS)[number]["key"];

export const INVITE_MERGE_TOKEN_KEYS: readonly InviteMergeTokenKey[] = INVITE_MERGE_TOKEN_FIELDS.map(
  (t) => t.key
);
export const MESSAGE_MERGE_TOKEN_KEYS: readonly MessageMergeTokenKey[] = MESSAGE_MERGE_TOKEN_FIELDS.map(
  (t) => t.key
);

/**
 * Estimated expansion headroom per merge token (chars reserved from the template budget).
 * Example: base 290 − firstName (20) = 270; with company too: 290 − 20 − 30 = 240.
 */
export const INVITATION_MERGE_TOKEN_RESERVE_CHARS: Record<InviteMergeTokenKey, number> = {
  firstName: 20,
  lastName: 25,
  company: 30,
  position: 45,
  location: 35,
};

const INVITE_MERGE_TOKEN_KEY_SET = new Set<string>(INVITE_MERGE_TOKEN_KEYS);

/** Count each invite merge token occurrence in the template (after normalizing braces). */
export function countInviteMergeTokenUsages(text: string): Partial<Record<InviteMergeTokenKey, number>> {
  const counts: Partial<Record<InviteMergeTokenKey, number>> = {};
  const source = normalizeSingleBraceMergeTokens(text, INVITE_MERGE_TOKEN_KEYS);
  const pattern = /\{\{([^}]+)\}\}/g;
  let match: RegExpExecArray | null = pattern.exec(source);
  while (match) {
    const inner = String(match[1] || "").trim();
    if (INVITE_MERGE_TOKEN_KEY_SET.has(inner)) {
      const key = inner as InviteMergeTokenKey;
      counts[key] = (counts[key] || 0) + 1;
    }
    match = pattern.exec(source);
  }
  return counts;
}

/** Dynamic template char limit based on which merge variables appear in the message. */
export function getInvitationTemplateMaxLength(text: string): number {
  const counts = countInviteMergeTokenUsages(text);
  let reserve = 0;
  for (const key of INVITE_MERGE_TOKEN_KEYS) {
    const usageCount = counts[key] || 0;
    if (usageCount > 0) {
      reserve += INVITATION_MERGE_TOKEN_RESERVE_CHARS[key] * usageCount;
    }
  }
  const maxLength = INVITATION_TEMPLATE_BASE_MAX_LENGTH - reserve;
  return Math.max(50, maxLength);
}

export function getInvitationTemplateLengthError(text: string): string | undefined {
  const message = String(text || "");
  if (!message.trim()) return undefined;
  const maxLength = getInvitationTemplateMaxLength(message);
  if (message.length <= maxLength) return undefined;
  return `Message cannot exceed ${maxLength} characters with the variables you've added.`;
}

/** `{key}` → `{{key}}` for known merge keys; leaves `{{key}}` unchanged. */
export function normalizeSingleBraceMergeTokens(
  text: string,
  allowedKeys: readonly string[] = MESSAGE_MERGE_TOKEN_KEYS
): string {
  const keys = allowedKeys.join("|");
  return text.replace(new RegExp(`\\{(${keys})\\}(?!\\})`, "g"), "{{$1}}");
}

export function findUnsupportedMergeTokens(
  text: string,
  allowedKeys: readonly string[] = MESSAGE_MERGE_TOKEN_KEYS
): string[] {
  const allowed = new Set(allowedKeys);
  const unsupported: string[] = [];
  const seen = new Set<string>();
  const source = String(text || "");
  const pattern = /\{\{([^}]+)\}\}/g;
  let match: RegExpExecArray | null = pattern.exec(source);
  while (match) {
    const inner = String(match[1] || "").trim();
    if (inner && !allowed.has(inner)) {
      const token = `{{${inner}}}`;
      if (!seen.has(token)) {
        seen.add(token);
        unsupported.push(token);
      }
    }
    match = pattern.exec(source);
  }
  return unsupported;
}

export function formatSupportedMergeTokensList(allowedKeys: readonly string[] = MESSAGE_MERGE_TOKEN_KEYS): string {
  return allowedKeys.map((key) => `{{${key}}}`).join(", ");
}

export function getMergeTokenValidationError(
  text: string,
  allowedKeys: readonly string[] = MESSAGE_MERGE_TOKEN_KEYS
): string | undefined {
  const unsupported = findUnsupportedMergeTokens(text, allowedKeys);
  if (unsupported.length === 0) return undefined;
  return `Unsupported variable${unsupported.length > 1 ? "s" : ""}: ${unsupported.join(", ")}. Supported tokens: ${formatSupportedMergeTokensList(allowedKeys)}.`;
}

type YupLikeTestContext = {
  createError: (params: { message: string }) => unknown;
};

/** Yup-compatible merge token test (cast to TestFunction at the schema call site). */
export function createMergeTokenYupTest(allowedKeys: readonly string[]) {
  return function mergeTokenYupTest(this: YupLikeTestContext, value: unknown) {
    const text = typeof value === "string" ? value : "";
    if (!text.trim()) return true;
    const error = getMergeTokenValidationError(text, allowedKeys);
    if (error) return this.createError({ message: error });
    return true;
  };
}
